import ArticleCard from "@/components/ui/ArticleCard";
import { toNewsArticle } from "@/lib/articleAdapter";
import { Link } from "@/lib/router-compat";
import { ArrowUpRight } from "lucide-react";
import { SectionLabel } from "./SectionLabel";
import SmartImage from "@/components/ui/SmartImage";
import { T } from "@/i18n/T";

interface Article { id: string; title: string; image_url?: string | null; source_name?: string | null; source_id?: string | null; }

/** Longest sentence in the first two paragraphs makes a decent pull quote. */
function pullQuote(paragraphs: string[]) {
  const pool = paragraphs.slice(0, 2).join(" ").split(/(?<=[.!?])\s+/);
  const best = pool.filter((s) => s.length > 60 && s.length < 190).sort((a, b) => b.length - a.length)[0];
  return best ?? null;
}

export function NarrativeSection({
  title,
  body,
  articles,
}: {
  title: string;
  body: string;
  articles: Article[];
}) {
  if (!body) return null;
  const paragraphs = body.split(/\n\n+/).filter(Boolean);
  const quote = pullQuote(paragraphs);

  return (
    <section className="mb-16">
      <SectionLabel className="text-primary"><T k="auto.narrative-of-the-week-60f4" /></SectionLabel>
      <div className="rounded-3xl border border-border bg-muted/30 p-6 md:p-10">
        <h2 className="mb-6 max-w-3xl font-display text-3xl leading-tight text-foreground md:text-4xl">{title}</h2>

        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="font-display text-lg leading-relaxed text-foreground/90">
              {paragraphs.map((p, i) => (
                <p
                  key={i}
                  className={
                    i === 0
                      ? "mb-4 first-letter:float-left first-letter:mr-2 first-letter:font-display first-letter:text-6xl first-letter:leading-[0.8] first-letter:text-foreground"
                      : "mb-4"
                  }
                >
                  {p}
                </p>
              ))}
            </div>
          </div>

          {quote && (
            <aside className="lg:col-span-1">
              <blockquote className="border-l-2 border-primary pl-5 font-display text-xl italic leading-snug text-foreground">
                “{quote}”
              </blockquote>
            </aside>
          )}
        </div>

        {articles.length > 0 && (
          <div className="mt-8 border-t border-border pt-6">
            <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <T k="auto.further-reading-1ba5" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 md:gap-4">
              {articles.map((a) => (
                <ArticleCard key={a.id} article={toNewsArticle(a)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
